import React from 'react';

import { Typography } from '@mui/material';
import { differenceInCalendarDays, differenceInSeconds, endOfDay, format, parse } from 'date-fns';
import { useTranslation } from 'react-i18next';

import styled from '../../../../styled';
import { colorsPalette } from '../../../../theme';
import { AssignmentForLearnerType } from '../../../../types';

interface UserLearningDueDateProps {
  dueDate?: string;
  completedDate?: string | null;
  completed?: boolean;
  entityNumber?: number | null;
  credits?: number | null;
  startDate?: string | null;
  className?: string;
  type?: AssignmentForLearnerType | null;
}

const UserLearningDueDate: React.FC<UserLearningDueDateProps> = ({
  dueDate,
  completedDate,
  completed,
  credits,
  entityNumber,
  startDate,
  className,
  type,
}) => {
  const { t } = useTranslation();
  const parsedDueDate = dueDate && parse(dueDate, 'yyyy-LL-dd HH:mm:ss', new Date());
  const parsedCompletedDate =
    completedDate && parse(completedDate, 'yyyy-LL-dd HH:mm:ss', new Date());
  const parsedStartDate = startDate && parse(startDate, 'yyyy-LL-dd HH:mm:ss', new Date());

  const difference = parsedDueDate
    ? differenceInCalendarDays(endOfDay(parsedDueDate), new Date())
    : 0;
  const isFinished = parsedDueDate
    ? differenceInSeconds(endOfDay(parsedDueDate), new Date()) <= 0
    : true;

  const isCpd = type === AssignmentForLearnerType.Cpd;
  const isHours = type === AssignmentForLearnerType.Hours;
  const isPathway = type === AssignmentForLearnerType.Pathway;
  const isVideo = type === AssignmentForLearnerType.Video;

  return (
    <DueDate
      data-testid="aslSubtitle"
      className={className}
      color={colorsPalette[completed ? 'green' : difference >= 0 ? 'violet' : 'failedError']}
    >
      {completed ? (
        <>
          {credits && (
            <>
              {isCpd && t('page.assignedLearnings.cpdCredit', { count: credits })}
              {isHours && t('page.assignedLearnings.hourCredit', { count: credits })}
              {' • '}
            </>
          )}
          {!!entityNumber && (
            <>
              {isPathway && t('common.pathway', { count: entityNumber })}
              {isVideo && t('common.videoCount', { count: entityNumber })}
              {' • '}
            </>
          )}
          {t('page.assignedLearnings.complete')}:{' '}
          {parsedCompletedDate && format(parsedCompletedDate, 'dd/MM/yyyy')}
        </>
      ) : (
        <>
          {credits && (
            <>
              {isCpd && t('page.assignedLearnings.cpdCredit', { count: credits })}
              {isHours && t('page.assignedLearnings.hourCredit', { count: credits })}
              {' • '}
            </>
          )}
          {!!entityNumber && (
            <>
              {isPathway && t('common.pathway', { count: entityNumber })}
              {isVideo && t('common.videoCount', { count: entityNumber })}
              {' • '}
            </>
          )}
          {!!parsedStartDate && (
            <>
              {t('page.assignedLearnings.start')}{' '}
              {parsedStartDate && format(parsedStartDate, 'dd/MM/yyyy')}
              {' • '}
            </>
          )}
          {t('page.assignedLearnings.due')} {parsedDueDate && format(parsedDueDate, 'dd/MM/yyyy')}
          {' • '}
          {isFinished
            ? t('page.assignedLearnings.notCompleted')
            : t('page.assignedLearnings.dayRemaining', { count: Math.abs(difference) })}
        </>
      )}
    </DueDate>
  );
};

export default UserLearningDueDate;

const DueDate = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'color',
})`
  color: ${({ color }) => color};
`;
